import {FC, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import {useIntl} from 'react-intl'
import {LazyLoadImage} from 'react-lazy-load-image-component'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {ItemProcessHistoryModel} from '../models/ItemProcessHistoryModel'
import {getAllItemProcessHistories} from '../redux/itemApi'
import {selectHistoryRefreshKey} from '../redux/itemRedux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import './itemProcessHistory.scss'

interface Props {
  itemId: number
  inline?: boolean
  selectedHistoryId?: number
  onSelectHistory?: (history: ItemProcessHistoryModel) => void
}

const fallbackImage = toAbsoluteUrl('/media/loading-bg.gif')

const resolveHistoryImageUrl = (path?: string) => {
  if (!path) {
    return undefined
  }

  if (/^(https?:|data:|blob:)/i.test(path)) {
    return path
  }

  const apiUrl = process.env.REACT_APP_API_URL
  if (!apiUrl) {
    return path
  }

  try {
    const origin = new URL(apiUrl).origin
    return `${origin}/${path.replace(/^\/+/, '')}`
  } catch (error) {
    return path
  }
}

const ItemProcessHistory: FC<Props> = ({itemId, inline = false, selectedHistoryId, onSelectHistory}) => {
  const intl = useIntl()
  const [processHistories, setProcessHistories] = useState<ItemProcessHistoryModel[]>([])
  const historyRefreshKey = useAppSelector(selectHistoryRefreshKey)

  useEffect(() => {
    let isActive = true

    const fetchProcessHistories = async () => {
      setProcessHistories([])
      try {
        const histories = await getAllItemProcessHistories(itemId)
        if (!isActive) {
          return
        }

        const sortedHistories = [...histories].sort((a, b) => {
          return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
        })
        setProcessHistories(sortedHistories)
      } catch (error) {
        if (isActive) {
          setProcessHistories([])
        }
      }
    }

    if (itemId > 0) {
      fetchProcessHistories()
    }

    return () => {
      isActive = false
    }
  }, [itemId, historyRefreshKey])

  const visibleHistories = useMemo(() => {
    const outputHistories = processHistories.filter((item) => !!item.output)
    return outputHistories.length > 1 ? outputHistories : []
  }, [processHistories])

  if (visibleHistories.length === 0) {
    return null
  }

  const dateTimeFormat = process.env.REACT_APP_DATE_TIME_FORMAT || 'YYYY/MM/DD HH:mm'

  const historyList = (
    <div className='text-center d-flex overflow-auto item-process-history item-process-history__list'>
      {visibleHistories.map((processHistory) => {
        const outputImageUrl = resolveHistoryImageUrl(processHistory.output) || fallbackImage
        return (
          <div
            key={processHistory.id}
            className={`item-process-history__item position-relative ${
              selectedHistoryId === processHistory.id ? 'item-process-history__item--active' : ''
            }`}
            title={`#${processHistory.processId} - ${moment(processHistory.createdAt).format(
              dateTimeFormat
            )}`}
            onClick={() => onSelectHistory?.(processHistory)}
          >
            <LazyLoadImage
              height={'120px'}
              placeholderSrc={fallbackImage}
              style={{
                paddingLeft: '20px',
                paddingRight: '20px',
                cursor: onSelectHistory ? 'pointer' : 'default',
              }}
              src={outputImageUrl}
              alt={`${intl.formatMessage({id: 'ITEM.VIEW.Output'})}-${processHistory.processId}`}
              effect='opacity'
              onError={(event: any) => {
                if (event?.target?.src !== fallbackImage) {
                  event.target.src = fallbackImage
                }
              }}
            />
            {selectedHistoryId === processHistory.id && (
              <i
                style={{
                  top: '10px',
                  right: '30px',
                }}
                className='position-absolute text-success fs-2 bi bi-check-circle-fill'
              ></i>
            )}
          </div>
        )
      })}
    </div>
  )

  if (inline) {
    return (
      <div className='card-footer py-5'>
        <h4 className='fw-bolder mb-4'>{intl.formatMessage({id: 'ItemDetail.Title.ITEM_REVISION'})}</h4>
        {historyList}
      </div>
    )
  }

  return (
    <div className='card mt-6'>
      <div className='card-header'>
        <h3 className='card-title fw-bolder'>
          {intl.formatMessage({id: 'ItemDetail.Title.ITEM_HISTORY'})}
        </h3>
      </div>
      <div className='card-footer py-5'>{historyList}</div>
    </div>
  )
}

export default ItemProcessHistory
